import { call, put, takeEvery } from "redux-saga/effects";

import { FeatureType } from "@/jenesys-types/data/enums";
import { AppError } from "@/jenesys-types/errors";

import { performFeatureAction } from "@/services/api";
import {
  fetchBanks,
  loadBankConnections
} from "@/store/features/clientSettings/connections/connections";

import {
  addBank,
  addBankFailure,
  addBankSuccess,
  bulkManualMatch,
  bulkManualMatchFailure,
  bulkManualMatchSuccess,
  createBankAccount,
  createBankAccountFailure,
  createBankAccountSuccess,
  deleteBankAccount,
  deleteBankAccountFailure,
  deleteBankAccountSuccess,
  fetchReconciliationList,
  fetchReconciliationListFailure,
  fetchReconciliationListSuccess,
  syncStatement,
  syncStatementFailure,
  syncStatementSuccess,
  uploadStatement,
  uploadStatementFailure,
  uploadStatementSuccess
} from "./reconciliation";

function* handleFetchReconciliationList(
  action: ReturnType<typeof fetchReconciliationList>
): Generator<any, void, any> {
  try {
    const { companyId, bankInstitutionId } = action.payload;
    const response = yield call(
      performFeatureAction,
      FeatureType.Reconciliation,
      "loadReconciliations",
      { companyId },
      bankInstitutionId
        ? { bank_institution_ids: [bankInstitutionId] }
        : undefined
    );
    yield put(fetchReconciliationListSuccess(response));
  } catch (error) {
    yield put(fetchReconciliationListFailure(error as AppError));
  }
}

function* handleAddBank(
  action: ReturnType<typeof addBank>
): Generator<any, void, any> {
  try {
    const { companyId, bankData, onSuccess } = action.payload;
    yield call(
      performFeatureAction,
      FeatureType.Reconciliation,
      "addBank",
      { companyId },
      undefined,
      bankData
    );
    yield put(addBankSuccess());
    if (onSuccess) onSuccess();
    yield put(fetchReconciliationList({ companyId }));
    yield put(fetchBanks());
  } catch (error) {
    yield put(addBankFailure(error as AppError));
  }
}

function* handleCreateBankAccount(
  action: ReturnType<typeof createBankAccount>
): Generator<any, void, any> {
  try {
    const { companyId, accountData, onSuccess } = action.payload;
    yield call(
      performFeatureAction,
      FeatureType.Reconciliation,
      "createBankAccount",
      { companyId },
      undefined,
      accountData
    );
    yield put(createBankAccountSuccess());
    if (onSuccess) onSuccess();
    yield put(fetchReconciliationList({ companyId }));
  } catch (error) {
    yield put(createBankAccountFailure(error as AppError));
  }
}

function* handleUploadStatement(
  action: ReturnType<typeof uploadStatement>
): Generator<any, void, any> {
  try {
    const { bankAccountId, files, onSuccess } = action.payload;
    const formData = new FormData();
    files.forEach((file: File) => {
      formData.append("files", file, file.name);
    });
    yield call(
      performFeatureAction,
      FeatureType.Reconciliation,
      "uploadBankStatement", // We'll add this to the API config if not present
      { accountId: bankAccountId },
      undefined,
      formData
    );
    yield put(uploadStatementSuccess());
    if (onSuccess) onSuccess();
  } catch (error) {
    yield put(uploadStatementFailure(error as AppError));
  }
}

function* handleSyncStatement(
  action: ReturnType<typeof syncStatement>
): Generator<any, void, any> {
  try {
    const { bankAccountId, documentId, onSuccess } = action.payload;
    yield call(
      performFeatureAction,
      FeatureType.Reconciliation,
      "convertStatementToTransactions",
      { accountId: bankAccountId, documentId }
    );
    yield put(syncStatementSuccess());
    if (onSuccess) onSuccess();
  } catch (error) {
    yield put(syncStatementFailure(error as AppError));
  }
}

function* handleDeleteBankAccount(
  action: ReturnType<typeof deleteBankAccount>
): Generator<any, void, any> {
  try {
    const { companyId, bankAccountId, onSuccess } = action.payload;
    yield call(
      performFeatureAction,
      FeatureType.Reconciliation,
      "deleteBankAccount",
      { companyId, accountId: bankAccountId }
    );
    yield put(deleteBankAccountSuccess());
    if (onSuccess) onSuccess();
    yield put(fetchReconciliationList({ companyId }));
    yield put(loadBankConnections(companyId));
    yield put(fetchBanks());
  } catch (error) {
    yield put(deleteBankAccountFailure(error as AppError));
  }
}

function* handleBulkManualMatch(
  action: ReturnType<typeof bulkManualMatch>
): Generator<any, void, any> {
  try {
    const {
      companyId,
      accountId,
      transactionIds,
      manualMatchReason,
      onSuccess
    } = action.payload;
    yield call(
      performFeatureAction,
      FeatureType.Reconciliation,
      "bulkManualMatch",
      { companyId, accountId },
      undefined,
      {
        transaction_ids: transactionIds,
        manual_match_reason: manualMatchReason
      }
    );
    yield put(bulkManualMatchSuccess());
    if (onSuccess) onSuccess();
  } catch (error) {
    yield put(bulkManualMatchFailure(error as AppError));
  }
}

export function* reconciliationSaga() {
  yield takeEvery(fetchReconciliationList.type, handleFetchReconciliationList);
  yield takeEvery(addBank.type, handleAddBank);
  yield takeEvery(createBankAccount.type, handleCreateBankAccount);
  yield takeEvery(uploadStatement.type, handleUploadStatement);
  yield takeEvery(syncStatement.type, handleSyncStatement);
  yield takeEvery(bulkManualMatch.type, handleBulkManualMatch);
  yield takeEvery(deleteBankAccount.type, handleDeleteBankAccount);
}

export default reconciliationSaga;
